import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  CreditCard,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  QrCode,
  Receipt,
  Settings,
  Shield,
  Tags,
  User,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { getAssetsUrl } from "../lib/api";
import { Logo } from "../components/Logo";
import { LegalComercioFooter } from "../components/legal/LegalComercioFooter";
import type { Anuncio } from "../types";

const NAV_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/dashboard/seguros", icon: Shield, label: "Mis seguros" },
  { to: "/dashboard/pilotos", icon: Users, label: "Mis pilotos" },
  { to: "/dashboard/recibos", icon: Receipt, label: "Mis Recibos" },
  { to: "/dashboard/modalidades", icon: Tags, label: "Mis modalidades" },
  { to: "/dashboard/qr", icon: QrCode, label: "QR y enlaces" },
  { to: "/dashboard/documentos", icon: FileText, label: "Documentos" },
  { to: "/dashboard/configuracion", icon: Settings, label: "Configuración" },
  { to: "/dashboard/tarjetas", icon: CreditCard, label: "Mis tarjetas" },
  { to: "/dashboard/cuenta", icon: User, label: "Mi Cuenta" },
];

export function ClienteLayout() {
  const { user, cliente, logout, isImpersonating, salirSuplantacion } = useAuth();
  const navigate = useNavigate();
  const [anuncios, setAnuncios] = useState<Anuncio[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    api
      .get<Anuncio[]>("/cliente/anuncios", { signal: controller.signal })
      .then((res) => {
        setAnuncios(Array.isArray(res.data) ? res.data.slice(0, 4) : []);
      })
      .catch((err) => {
        if (
          err?.name !== "CanceledError" &&
          err?.code !== "ERR_CANCELED"
        ) {
          setAnuncios([]);
        }
      });

    return () => controller.abort();
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen min-w-0 bg-[#F5F7FA]">
      {menuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-label="Cerrar menú"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 flex w-60 flex-col bg-[#1A3AB8] shadow-xl transition-transform duration-200 lg:translate-x-0 lg:shadow-none ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-label="Navegación del cliente"
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 bg-white px-4">
          <Logo className="h-8 max-w-[170px] origin-left scale-[1.75] object-contain lg:scale-[2]" />

          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 lg:hidden"
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <nav className="space-y-0.5 p-3">
            {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10 ${
                    isActive ? "bg-white/20" : ""
                  }`
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {anuncios.length > 0 && (
            <div className="border-t border-white/10 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-white/70">
                Anuncios
              </p>

              <div className="space-y-2">
                {anuncios.map((anuncio) => (
                  <a
                    key={anuncio.id}
                    href={anuncio.enlace_url || "#"}
                    target={anuncio.enlace_url ? "_blank" : undefined}
                    rel={
                      anuncio.enlace_url
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="block overflow-hidden rounded-lg bg-white/10 transition hover:bg-white/15"
                  >
                    <div className="aspect-video w-full overflow-hidden bg-white/5">
                      <img
                        src={getAssetsUrl(anuncio.imagen_url)}
                        alt=""
                        className="h-full w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </div>

                    {anuncio.texto && (
                      <p className="line-clamp-2 p-2 text-xs text-white/90">
                        {anuncio.texto}
                      </p>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-col lg:pl-[240px]">
        <div className="sticky top-0 z-10">
          {isImpersonating && (
            <div className="flex flex-col gap-2 bg-[#E67E22] px-4 py-2 text-sm font-semibold text-white sm:flex-row sm:items-center sm:justify-between">
              <span className="break-words">
                ⚠ Estás viendo la plataforma como: {user?.email}
              </span>

              <button
                type="button"
                onClick={salirSuplantacion}
                className="min-h-10 rounded px-3 py-1.5 text-left font-medium text-white hover:bg-white/20"
              >
                ← Volver a mi sesión de admin
              </button>
            </div>
          )}

          <header className="grid min-h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-gray-200 bg-white px-3 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] lg:px-8">
            <div className="hidden lg:block" aria-hidden="true" />
            <div className="flex min-w-0 items-center gap-2 lg:hidden">
              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100"
                aria-label="Abrir menú"
                aria-expanded={menuOpen}
              >
                <Menu className="h-5 w-5" />
              </button>

              <Logo
                className="h-7 max-w-[120px] origin-left scale-150 object-contain"
                containerClassName="min-w-0"
              />
            </div>

            {cliente?.razon_social && (
              <div className="order-last col-span-2 min-w-0 pb-2 text-center lg:order-none lg:col-span-1 lg:py-2">
                <span className="block break-words text-sm font-semibold text-[#2C2C2C] lg:text-base" title={cliente.razon_social}>
                  {cliente.razon_social}
                </span>
              </div>
            )}

            <div className="ml-auto flex min-w-0 items-center gap-2 sm:gap-4 lg:col-start-3">
              <div className="hidden min-w-0 max-w-[360px] flex-col items-end leading-tight sm:flex">
                <span
                  className="max-w-full truncate text-xs text-gray-600"
                  title={user?.email}
                >
                  {user?.email}
                </span>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm text-[#2C2C2C] hover:bg-gray-100"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Cerrar sesión</span>
              </button>
            </div>
          </header>
        </div>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>

          <LegalComercioFooter />
        </main>
      </div>
    </div>
  );
}
